import React, { useState, useEffect } from 'react';
import ControlBoard from '../../components/ControlBoard';
import RangeSlider from '../../components/inputs/RangSlider';
import produce from 'immer';
import { getRelativeColorByPercent } from '../../utils/colorUtils';
import ColorPicker from '../../components/ColorPicker';
import Button, { BUTTON_TYPES } from '../../components/Button';
import Label from '../../components/Label';


export default function LightModeController(props) {
  const { containerStyleData, setContainerStyleData, toggleCardController, cardStyle, setCardStyle } = props;
  const [cardBgIntensity, setcardBgIntensity] = useState(-10);
  const [intensity, setIntensity] = useState(0);
  const [elevation, setElevation] = useState(0);
  const [blurDistance, setBlurDistance] = useState(0);
  const [colorPickerVisible, toggleColorPicker] = useState(false);

  useEffect(() => {
    onBackgroundColorChange({ hex: '#ffffff' });
  }, []);

  const onBackgroundColorChange = (color) => {
    const newColor = color.hex;
    const newContainerStyleData = produce(containerStyleData, (draft) => {
      draft.style.backgroundColor = newColor;
      draft.style.backgroundImage = `linear-gradient(to right, ${newColor})`;
      draft.bgColorsList = [newColor];
    });

    const cardBgColor1 = getRelativeColorByPercent(newColor, cardBgIntensity);
    const cardBgColor2 = getRelativeColorByPercent(newColor, cardBgIntensity * 2);

    const newCardStyleData = getCardShadowChangeStyle(newColor, intensity);
    const newStyleData = produce(newCardStyleData, (draft) => {
      draft.bgColorsList = [cardBgColor1, cardBgColor2];
      draft.style.backgroundImage = `linear-gradient(${draft.backgroundImageAngle}deg, ${cardBgColor1}, ${cardBgColor2})`;
    });
    setCardStyle(newStyleData);
    setContainerStyleData(newContainerStyleData);
  };

  const handleCardBgIntensity = (e) => {
    const newBgIntensity = Number(e.target.value) * -1; // since it is light mode, card should be darker 
    const cardBgColor1 = getRelativeColorByPercent(containerStyleData.style.backgroundColor, newBgIntensity);
    const cardBgColor2 = getRelativeColorByPercent(containerStyleData.style.backgroundColor, newBgIntensity * 2);
    const newStyleData = produce(cardStyle, (draft) => {
      draft.bgColorsList = [cardBgColor1, cardBgColor2];
      draft.style.backgroundImage = `linear-gradient(${draft.backgroundImageAngle}deg, ${cardBgColor1}, ${cardBgColor2})`;
    });
    setCardStyle(newStyleData);
    setcardBgIntensity(newBgIntensity * -1); // intensity is shown positive value to the user
  };

  const getCardShadowChangeStyle = (backgroundColor, intensityPercent) => {
    const lighterShadowColor = getRelativeColorByPercent(backgroundColor, intensityPercent);
    const darkerShadowColor = getRelativeColorByPercent(backgroundColor, intensityPercent * -1);
    const newStyle = produce(cardStyle, (draft) => {
      draft.boxShadow[0][4] = darkerShadowColor;
      draft.boxShadow[1][4] = lighterShadowColor;
      const tempBoxShadow0 = draft.boxShadow[0];
      const tempBoxShadow1 = draft.boxShadow[1];
      draft.style.boxShadow = `
        ${tempBoxShadow0[0]}px ${tempBoxShadow0[1]}px ${tempBoxShadow0[2]}px ${tempBoxShadow0[3]}px ${tempBoxShadow0[4]}, 
        ${tempBoxShadow1[0]}px ${tempBoxShadow1[1]}px ${tempBoxShadow1[2]}px ${tempBoxShadow1[3]}px ${tempBoxShadow1[4]}
      `;
    });
    return newStyle;
  };

  const handleIntensityChange = (e) => {
    const intensityPercent = Number(e.target.value);
    setCardStyle(getCardShadowChangeStyle(containerStyleData.style.backgroundColor, intensityPercent));
    setIntensity(intensityPercent);

  };

  const handleElevationChange = (e) => {
    const elevation = Number(e.target.value);
    const newStyle = produce(cardStyle, (draft) => {
      draft.boxShadow[0][0] = elevation;
      draft.boxShadow[0][1] = elevation * -1;
      draft.boxShadow[0][2] = elevation * 2;
      draft.boxShadow[1][0] = elevation * -1;
      draft.boxShadow[1][1] = elevation;
      draft.boxShadow[1][2] = elevation * 2;
      const tempBoxShadow0 = draft.boxShadow[0];
      const tempBoxShadow1 = draft.boxShadow[1];
      draft.style.boxShadow = `${tempBoxShadow0[0]}px ${tempBoxShadow0[1]}px ${tempBoxShadow0[2]}px ${tempBoxShadow0[3]}px ${tempBoxShadow0[4]}, ${tempBoxShadow1[0]}px ${tempBoxShadow1[1]}px ${tempBoxShadow1[2]}px ${tempBoxShadow1[3]}px ${tempBoxShadow1[4]}`;
    });
    setCardStyle(newStyle);
    setElevation(elevation);
    setBlurDistance(elevation * 2);
  }

  const handleBlurDistanceChange = (e) => {
    const blur = Number(e.target.value);
    const newStyle = produce(cardStyle, (draft) => {
      draft.boxShadow[0][2] = blur;
      draft.boxShadow[1][2] = blur;
      const tempBoxShadow0 = draft.boxShadow[0];
      const tempBoxShadow1 = draft.boxShadow[1];
      draft.style.boxShadow = `
        ${tempBoxShadow0[0]}px ${tempBoxShadow0[1]}px ${tempBoxShadow0[2]}px ${tempBoxShadow0[3]}px ${tempBoxShadow0[4]}, 
        ${tempBoxShadow1[0]}px ${tempBoxShadow1[1]}px ${tempBoxShadow1[2]}px ${tempBoxShadow1[3]}px ${tempBoxShadow1[4]}
      `;
    });
    setCardStyle(newStyle);
    setBlurDistance(blur);
  };

  return (
    <ControlBoard headingLabel="Light Mode Setting" doneText="Finish" showCancel={false} onDone={() => toggleCardController(false)}>
      {colorPickerVisible &&
        <ColorPicker
          color={containerStyleData.style.backgroundColor}
          onChange={onBackgroundColorChange}
          onDone={() => toggleColorPicker(false)}
        />
      }
      {!colorPickerVisible && (
        <React.Fragment>
          <Label style={{ display: 'inline' }}>Change Background Color </Label>
          <Button
            type={BUTTON_TYPES.PILLS}
            label={containerStyleData.style.backgroundColor}
            style={{ backgroundColor: containerStyleData.style.backgroundColor, marginLeft: 5, marginTop: 5 }}
            onClick={() => toggleColorPicker(true)}
          />
          <RangeSlider label={`Intensity of Card Background (${cardBgIntensity}%)`} min={0} max={100} value={cardBgIntensity} onChange={handleCardBgIntensity} />
          <Label label="Adjust Shadow" />
          <RangeSlider label={`Intensity (${intensity}%)`} min={0} max={200} value={intensity} onChange={handleIntensityChange} />
          <RangeSlider label={`Elevation (${elevation}px)`} min={0} max={100} value={elevation} onChange={handleElevationChange} />
          <RangeSlider label={`Blur Distance (${blurDistance}px)`} min={0} max={200} value={blurDistance} onChange={handleBlurDistanceChange} />
        </React.Fragment>
      )}
    </ControlBoard>
  );
}
